import React, { useEffect, useState } from 'react';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import { toastr } from 'react-redux-toastr';
import api from '../../api';
import { useForm } from 'react-hook-form';
import 'bootstrap/dist/css/bootstrap.min.css';
import ContainerLoader from '../loader/container-loader';
import styles from '../wallOfFrame/wallOfFrame.module.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

const HubSpocForm = () => {
    const [isfetching, setIsfetching] = useState(false);
    const [showForm, setShowForm] = useState(true);
    const [allUsers, setAllUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [inputValue, setInputValue] = useState('');

    const location = useLocation();
    const { state } = useLocation();
    const navigate = useNavigate();

    const intialData = location.state?.intialData || {};
    const currentId = location.state?.currentId || {};
    const isEdit = state ? state.isEdit : false;
    const hubs = location.state?.hubs || {};

    const authState = useSelector((state) => state.authUser);
    const userRoles = authState?.userRoles || [];
    const hasRole = (roleId) => userRoles.some(role => role.role_id === roleId);

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setError,
        clearErrors,
        setValue,
    } = useForm();

    // ✅ Fetch employee list
    useEffect(() => {
        const fetchAllUsers = async () => {
            try {
                setIsfetching(true);
                setShowForm(false);
                const response = await api.clientsinformation.getDetails({
                    resource: `api/zoho/getAllEmployees`,
                });
                const details = response?.data?.details || [];
                setAllUsers(details);
            } catch (error) {
                console.error('Error fetching user data:', error);
                toastr.error('Error', 'Failed to fetch employee list');
            } finally {
                setIsfetching(false);
                setShowForm(true);
            }
        };

        fetchAllUsers();
    }, []);

    // ✅ Handle email input change
    const handleInputChange = (e) => {
        const value = e.target.value.trim();
        setInputValue(value);
        setValue('spocemail', value);

        if (!value) {
            setFilteredUsers([]);
            setSelectedUser(null);
            setValue('spocname', '');
            clearErrors('spocemail');
            return;
        }

        const filtered = allUsers.filter((user) =>
            user.emailid.toLowerCase().includes(value.toLowerCase()),
        );
        setFilteredUsers(filtered);

        if (filtered.length === 0) {
            setError('spocemail', {
                type: 'manual',
                message: 'No matching users found',
            });
        } else {
            clearErrors('spocemail');
        }
    };

    // ✅ When a suggestion is selected
    const handleSelect = (email) => {
        const user = allUsers.find((u) => u.emailid === email);
        setSelectedUser(user);
        setInputValue(email);
        setValue('spocemail', email);
        setValue('spocname', `${user.firstname} ${user.lastname}`);
        setFilteredUsers([]);
        clearErrors('spocemail');
    };

    // ✅ Pre-fill data when editing
    useEffect(() => {
        if (intialData) {
            Object.keys(intialData).forEach((key) => {
                if (key === 'spocname') {
                    const temp = intialData.spocname.split(' ');
                    setSelectedUser({ firstname: temp[0], lastname: temp[1] });
                } else {
                    setValue(key, intialData[key]);
                    if (key === 'spocemail') setInputValue(intialData[key]); // ✅ Sync local state
                }
            });
        }
    }, [intialData, setValue]);

    // ✅ Handle submit (Add / Edit)
    const handleData = async (formData) => {
        if (isEdit) {
            try {
                setIsfetching(true);
                setShowForm(false);
                const request = { resource: `api/hubspoc/${currentId}` };
                const editResponse = await api.hubspoc.updateDetails(request, { ...formData });
    
                if (editResponse.data.status === 200) {
                    toastr.success('Success', 'Record updated successfully!');
                    reset(); // ✅ Only reset on success
                    navigate('/managehubspoc');
                } else {
                    toastr.error('Error', editResponse.data.message || 'Failed to update record');
                    setInputValue(formData.spocemail); // ✅ Restore email input
                }
            } catch (error) {
                console.error(error.message);
                toastr.error('Error', 'Something went wrong while updating');
                setInputValue(formData.spocemail); // ✅ Keep email visible
            } finally {
                setIsfetching(false);
                setShowForm(true);
            }
        } else {
            try {
                const request = { resource: 'api/hubspoc' };
                setIsfetching(true);
                setShowForm(false);
                const res = await api.hubspoc.addDetails(request, { ...formData });
    
                if (res.data.status === 200) {
                    toastr.success('Success', 'Record added Successfully!');
                    reset(); // ✅ Reset only on success
                    navigate('/managehubspoc');
                } else {
                    toastr.error('Error', res.data.message || 'Failed to add record');
                    setInputValue(formData.spocemail); // ✅ Keep entered email on error
                }
            } catch (error) {
                console.error(error.message);
                toastr.error('Error', 'Something went wrong while adding');
                setInputValue(formData.spocemail); // ✅ Keep email on network failure
            } finally {
                setIsfetching(false);
                setShowForm(true);
            }
        }
    };

    // ✅ Role-based access
    useEffect(() => {
        if (!hasRole(6) && !hasRole(10)) {
            navigate('/home');
        }
    }, [userRoles, navigate]);

    return (
        <div className={`${styles.dataTableWrapper} ${styles.formWrapper} mx-auto p-2`}>
            <h4 className="my-4 pagetitle">Hub SPOC Details Form</h4>
            {isfetching && <ContainerLoader />}
            {showForm && (
                <div className="row">
                    <div className="col-12">
                        <div className="walloffame_form w-sm-100 mx-auto">
                            <Form onSubmit={handleSubmit(handleData)} className="d-flex flex-wrap justify-content-between">
                                
                                {/* Hub Dropdown */}
                                <div className="col-12 col-md-6 p-2">
                                    <Form.Group>
                                        <Form.Label>
                                            Select Hub<span className="text-danger">*</span>
                                        </Form.Label>
                                        <Form.Select
                                            name="hubid"
                                            {...register('hubid', { required: 'Hub is required' })}
                                            className="mb-0"
                                            aria-label="Default select example"
                                            placeholder="Select hub"
                                        >
                                            <option value="">Select Hub</option>
                                            {hubs?.map((dept, index) => (
                                                <option key={index} value={dept.id}>
                                                    {dept.hubname}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        {errors.hubid && <p style={{ color: 'red' }}>{errors.hubid.message}</p>}
                                    </Form.Group>
                                </div>

                                {/* SPOC Email */}
                                <div className="col-12 col-md-6 p-2">
                                    <Form.Group className="mb-3 position-relative">
                                        <Form.Label>
                                            SPOC Email<span className="text-danger">*</span>
                                        </Form.Label>
                                        <Form.Control
                                            name="spocemail"
                                            type="text"
                                            {...register('spocemail', {
                                                required: 'Email is required',
                                                pattern: {
                                                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                                    message: 'Invalid email format',
                                                },
                                            })}
                                            placeholder="Enter Here"
                                            autoComplete="off"
                                            value={inputValue}
                                            onChange={handleInputChange}
                                        />
                                        {errors.spocemail && <p style={{ color: 'red' }}>{errors.spocemail.message}</p>}

                                        {filteredUsers.length > 0 && (
                                            <div className="dropdownSuggestions">
                                                {filteredUsers.map((user) => (
                                                    <div
                                                        key={user.employeeid}
                                                        className="dropdown-item"
                                                        onClick={() => handleSelect(user.emailid)}
                                                    >
                                                        {user.firstname} {user.lastname} ({user.emailid})
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </Form.Group>
                                </div>

                                {/* SPOC Name */}
                                <div className="col-12 col-md-6 p-2">
                                    <Form.Group className="mb-3">
                                        <Form.Label>SPOC Name</Form.Label>
                                        <Form.Control
                                            name="spocname"
                                            value={
                                                selectedUser
                                                    ? `${selectedUser.firstname} ${selectedUser.lastname}`
                                                    : ''
                                            }
                                            disabled
                                        />
                                    </Form.Group>
                                </div>

                                {/* Buttons */}
                                <div className="d-flex justify-content-end col-12 p-2">
                                    <Button
                                        variant="secondary"
                                        className="me-2"
                                        onClick={() => navigate('/managehubspoc')}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="reset"
                                        variant="secondary"
                                        className="mx-2"
                                        onClick={() => {
                                            reset();
                                            setSelectedUser(null);
                                            setInputValue('');
                                        }}
                                    >
                                        Reset
                                    </Button>
                                    <Button type="submit" variant="primary" className="ms-2">
                                        Save
                                    </Button>
                                </div>
                            </Form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HubSpocForm;
